import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@kidshome/ui';
import { Spinner } from '@/shared/ui/icons';
import { useOutbox } from './use-outbox';
import { outboxController } from './outbox-controller';

/** How long the "All changes synced" confirmation stays visible before auto-dismissing. */
const SYNCED_FLASH_MS = 3_000;

/**
 * The offline / sync surface (ADR-0018). Four kind, non-blocking, MUTUALLY EXCLUSIVE cues (at most one
 * of these four renders at a time), plus an independent failure toast:
 *   - an OFFLINE banner ("changes are saved and will sync") while disconnected,
 *   - a "Syncing…" pill while a drain is actively in flight,
 *   - a "N changes waiting to sync" badge when the queue is non-empty but no drain is running,
 *   - a brief "All changes synced" confirmation right after the queue empties cleanly,
 *   - a sync-FAILED toast PER permanently-rejected mutation (a 4xx) — every failure is surfaced,
 *     never silently dropped (a single drain can reject several); dismissing acknowledges one. This one
 *     is independent of the other four and stacks alongside them.
 * No shaming language; the economy is server-authoritative so the copy reassures rather than alarms.
 */
export function OfflineStatus(): React.ReactElement | null {
  const { t } = useTranslation('pwa');
  const { online, pending, syncing, failures } = useOutbox();

  const [showSynced, setShowSynced] = useState(false);
  // A "busy" stretch is pending>0 OR syncing — tracked as a SPAN, not a single transition, because
  // `pending` hitting 0 and `syncing` flipping to false are two SEPARATE snapshot updates (refreshCount
  // resolves before the controller sets `syncing: false`), so naively comparing pending's before/after
  // on its own misses the transition (it lands on the render where pending=0 but syncing is still
  // true). Whether the span actually had anything queued / surfaced a failure is accumulated across the
  // whole span too, for the same reason (a drain that dips pending to 0 mid-way while still syncing, or
  // surfaces a failure a render before syncing finally clears, must still be remembered at settle time).
  const wasBusyRef = useRef(pending > 0 || syncing);
  const hadPendingDuringBusyRef = useRef(pending > 0);
  const failedDuringBusyRef = useRef(false);
  const prevFailureCountRef = useRef(failures.length);

  useEffect(() => {
    const isBusyNow = pending > 0 || syncing;
    if (pending > 0) hadPendingDuringBusyRef.current = true;
    if (failures.length > prevFailureCountRef.current) failedDuringBusyRef.current = true;
    prevFailureCountRef.current = failures.length;

    const justSettled = wasBusyRef.current && !isBusyNow;
    wasBusyRef.current = isBusyNow;
    if (!justSettled) return undefined;

    const hadPending = hadPendingDuringBusyRef.current;
    const hadFailure = failedDuringBusyRef.current;
    hadPendingDuringBusyRef.current = false;
    failedDuringBusyRef.current = false;

    // Only flash "synced" if the span that just ended actually had something queued (a no-op drain —
    // e.g. the boot-time replay on an empty queue — flips syncing true->false without ever queuing
    // anything, and must NOT claim a confirmation for work that never existed) AND finished clean (a
    // failure surfaced anywhere in the span shouldn't pair a cheerful confirmation with a failure toast).
    if (hadPending && !hadFailure) {
      setShowSynced(true);
      const timer = window.setTimeout(() => setShowSynced(false), SYNCED_FLASH_MS);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [pending, syncing, failures.length]);

  const showBanner = !online;
  const showSyncing = !showBanner && syncing && pending > 0;
  const showBadge = !showBanner && !showSyncing && pending > 0;
  const showSyncedFlash = !showBanner && !showSyncing && !showBadge && showSynced;
  const showFailures = failures.length > 0;

  if (!showBanner && !showSyncing && !showBadge && !showSyncedFlash && !showFailures) return null;

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-[calc(var(--bottom-nav-height)+0.75rem)] z-40 flex flex-col items-center gap-2 px-4">
      {showBanner && (
        <div
          role="status"
          aria-live="polite"
          className="pointer-events-auto max-w-md rounded-pill border border-warning bg-warning-fg px-4 py-2 text-center text-xs font-semibold text-warning"
        >
          {t('offline.banner')}
        </div>
      )}
      {showSyncing && (
        <div
          role="status"
          aria-live="polite"
          className="pointer-events-auto flex items-center gap-2 rounded-pill border border-border bg-surface px-4 py-2 text-xs font-semibold text-text-muted shadow-sm"
        >
          <Spinner size={14} />
          {t('offline.syncing', { count: pending })}
        </div>
      )}
      {showBadge && (
        <div
          role="status"
          aria-live="polite"
          className="pointer-events-auto rounded-pill border border-border bg-surface px-4 py-2 text-xs font-semibold text-text-muted shadow-sm"
        >
          {t('offline.pending', { count: pending })}
        </div>
      )}
      {showSyncedFlash && (
        <div
          role="status"
          aria-live="polite"
          className="pointer-events-auto rounded-pill border border-success bg-success-fg px-4 py-2 text-xs font-semibold text-success"
        >
          {t('offline.synced')}
        </div>
      )}
      {failures.map((failure) => (
        <div
          key={failure.idempotencyKey}
          role="alert"
          className="pointer-events-auto flex max-w-md items-center gap-3 rounded-card border border-danger bg-surface p-3 shadow-lg"
        >
          <div className="flex min-w-0 flex-col">
            <span className="text-sm font-bold text-danger">{t('offline.syncFailedTitle')}</span>
            <span className="text-xs text-text-muted">{t('offline.syncFailedMessage')}</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="ms-auto"
            onClick={() => outboxController.clearFailure(failure.idempotencyKey)}
          >
            {t('offline.syncFailedAction')}
          </Button>
        </div>
      ))}
    </div>
  );
}
